import { toast } from "$service/toast";
import { get } from "svelte/store";
import { connected } from "./sse";
import { pruneRequestRecord, requests_component_lookup } from "./store";
import { apiFetch, BASE_URL, endSessionIfExpired } from "./api";

console.log("Using BASE_URL:", BASE_URL);

export async function fetchFromApi(endpoint, options = {}) {
    const res = await apiFetch(endpoint, options);
    const bodyText = await res.text();             // read raw text
    let data;
    try {                                          // try parse JSON
        data = JSON.parse(bodyText);
    } catch {
        data = bodyText;                             // fallback to text
    }

    return data;
}

// This is a new endpoint to reduce the complexity of API calls
export async function fetch_api(endpoint, method, query, body) {
    const query_string = new URLSearchParams(query).toString();
    const endpoint_with_params = query_string ? `${endpoint}?${query_string}` : endpoint;

    let options = {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        }
    }
    if (body) {
        options = { ...options, body: JSON.stringify(body) }
    }

    const response = await apiFetch(endpoint_with_params, options);
    if (!response.ok) {
        const result = await response.text();
        toast(JSON.parse(result).detail.message, "error");
        throw result;
    }
    return await response.json();
}



export async function earlyAccess(entry) {
    return await fetchFromApi(`/early_access`, {

        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'access-control-allow-credentials': 'true',
            'vary': 'Origin'
        },
        body: JSON.stringify({
            name: entry.name,
            email: entry.email,
            company_name: entry.company_name
        })
    });
}


export async function getUser() {
    return await fetchFromApi(`/me`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function getSystemPrompt(prompt_address) {
    // prompt_address is a list of strings, so encode for query param
    const params = new URLSearchParams();
    prompt_address.forEach((item) => {
        params.append('prompt_address', item);
    });
    return await fetchFromApi(`/system_prompt?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
    });
}


export async function logout() {
    const resp = await fetchFromApi(`/logout`, {
        method: 'POST',
        credentials: 'include',
    });
    return resp;
}

export async function login(formData) {
    const resp = await fetchFromApi(`/login`, {
        method: 'POST',
        credentials: 'include',
        body: formData
    });
    return resp;
}

export async function register(email, password) {
    const resp = await fetchFromApi(`/register`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            password
        })
    });
    return resp;
}

export async function customerSupport(inquiry) {
    return await fetchFromApi(`/customer_support`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ...inquiry
        })
    });
}




export async function change_password_request(email) {

    return await fetchFromApi(`/change-password-request`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email
        })
    });
}


export async function verify(email, code) {

    return await fetchFromApi(`/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            code
        })
    });
}


export async function change_password(email, new_password, temporary_token) {

    return await fetchFromApi(`/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            new_password,
            temporary_token
        })
    });
}


export async function send_email(email, subject, message) {

    return await fetchFromApi(`/contact_form`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            subject,
            message
        })
    });
}



export async function get_organization_admin() {
    return await fetchFromApi(`/organization_admin`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        }
    });
}



export async function create_client_credentials() {
    return await fetchFromApi(`/create_client_credentials`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        }
    });
}



export async function get_azure_token() {
    return await fetchFromApi(`/speech/token`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        }
    });
}




// Responses arrive over SSE, so a request sent while the stream is down loses
// its reply. Waiting for the connection is right; waiting forever is not.
const SSE_WAIT_TIMEOUT_MS = 15000;

function waitForSseConnection(timeout_ms = SSE_WAIT_TIMEOUT_MS) {
    if (get(connected) === "true") return Promise.resolve();
    return new Promise((resolve, reject) => {
        const deadline = Date.now() + timeout_ms;
        const timer = setInterval(() => {
            if (get(connected) === "true") {
                clearInterval(timer);
                resolve();
            } else if (Date.now() >= deadline) {
                clearInterval(timer);
                reject(new Error("No live connection to the server — request not sent"));
            }
        }, 100);
    });
}

export async function make_request(payload, component_address, target_component_address) {
    // The previous unbounded retry loop resolved this promise immediately and
    // fired the request whenever the connection happened to come back. That
    // showed up as a click doing nothing for 10-30s, and it meant a caller's
    // try/catch could never observe a failure. Fail fast instead.
    try {
        await waitForSseConnection();
    } catch (error) {
        // Most call sites don't catch, so surface the failure here; rethrow
        // for the ones that reset their own loading state. An expired login
        // goes to the login screen instead of this toast.
        if (!(await endSessionIfExpired())) {
            toast.push("No connection to the server — please try again", { classes: ["warn"] });
        }
        throw error;
    }
    console.log("%cMaking request %c" + JSON.stringify(payload) + "%c request_id: %c" + payload.request_id, "color: #CF0000; font-weight: bold", "color: #000000", "color: #CF0000; font-weight: bold", "color: #000000");
    console.log("target_component_address ", target_component_address);
    requests_component_lookup.update((state) => {
        pruneRequestRecord(state);
        state[payload.request_id] = {
            "request_origin_component_address": component_address,
            "target_component_address": target_component_address,
            "status": "pending",
            "created_at": Date.now()
        };
        return state;
    });
    return await fetchFromApi(`/request`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'access-control-allow-credentials': 'true',
            'vary': 'Origin'
        },
        body: JSON.stringify(
            payload
        ),
        credentials: 'include'
    });

}

// Cooper Parry: classify how much the draft should elaborate on the user's notes.
// Runs a single inline LLM call server-side and returns { level: "stick"|"some"|"a_lot" }.
export async function classifyDraftNotes(request_id, module = "draft") {
    return await fetchFromApi(`/request`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            type: "ask_llm",
            sub_type: "classify_draft_notes",
            request_id,
            payload: { module },
        }),
        credentials: 'include'
    });
}

// Organizations API
export async function getOrganizations(page = 1, pageSize = 20) {
    return await fetchFromApi(`/organizations?page=${page}&page_size=${pageSize}`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function createOrganization(organizationData) {
    return await fetchFromApi(`/organizations`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(organizationData)
    });
}

export async function updateOrganization(organizationId, organizationData) {
    return await fetchFromApi(`/organizations/${organizationId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(organizationData)
    });
}

export async function deleteOrganization(organizationId) {
    return await fetchFromApi(`/organizations/${organizationId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

// Users API
export async function getAllUsers(page = 1, pageSize = 20, search = '') {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    return await fetchFromApi(`/users?page=${page}&page_size=${pageSize}${searchParam}`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function updateUserStatus(userId, enabled) {
    return await fetchFromApi(`/users/${userId}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled })
    });
}

export async function updateUser(userId, userData) {
    return await fetchFromApi(`/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
    });
}

export async function updateUserAdminStatus(organizationId, userId, isAdmin) {
    return await fetchFromApi(`/organizations/${organizationId}/admin/${userId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_admin: isAdmin })
    });
}

export async function updateUserOrganization(userId, userData) {
    // Try to update user with organization_id - backend may accept it even if not in schema
    return await fetchFromApi(`/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
    });
}

// Get organization details by ID
export async function getOrganization(organizationId) {
    return await fetchFromApi(`/organizations/${organizationId}`, {
        method: 'GET',
        credentials: 'include',
    });
}

// Get users in an organization
export async function getOrganizationUsers(organizationId) {
    return await fetchFromApi(`/organizations/${organizationId}/users`, {
        method: 'GET',
        credentials: 'include',
    });
}

export function create_billing_portal_session() {
    // Navigate directly to the backend endpoint — the backend returns a 302
    // redirect to Stripe's billing portal. Using window.location avoids CORS
    // issues that occur when fetch() tries to follow the redirect to Stripe.
    window.location.href = `${BASE_URL}/payments/billing_portal`;
}

// Organization MFA management (super admin only)
// mfaMethod is 'passkey' or 'totp'; omit it to keep whichever the org has.
// Beta features a system admin can switch on per organisation.
export async function getFeatureCatalogue() {
    return await fetchFromApi(`/features`, { method: 'GET', credentials: 'include' });
}

export async function updateGlobalFeature(featureId, enabled) {
    return await fetchFromApi(`/features/${featureId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
    });
}

export async function updateOrganizationFeatures(organizationId, features) {
    return await fetchFromApi(`/organizations/${organizationId}/features`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features })
    });
}

export async function updateOrganizationMfa(organizationId, mfaRequired, mfaMethod = null) {
    const body = { mfa_required: mfaRequired };
    if (mfaMethod) {
        body.mfa_method = mfaMethod;
    }
    return await fetchFromApi(`/organizations/${organizationId}/mfa`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
    });
}

// Authenticator app (TOTP) management
export async function getTotpStatus() {
    return await fetchFromApi(`/totp/status`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function deleteTotpCredential() {
    const res = await apiFetch(`/totp/credentials`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok) {
        const err = new Error();
        err.status = res.status;
        try { err.body = await res.json(); } catch { err.body = null; }
        throw err;
    }
    return await res.json();
}

// Super admin only: clear a user's authenticator app after they lose their phone
export async function resetUserTotp(userId) {
    const res = await apiFetch(`/totp/credentials/user/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok) {
        const err = new Error();
        err.status = res.status;
        try { err.body = await res.json(); } catch { err.body = null; }
        throw err;
    }
    return await res.json();
}

// Signed-in devices (sessions) for the profile page
export async function getMySessions() {
    return await fetchFromApi(`/sessions`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function signOutSession(sessionId) {
    return await fetchFromApi(`/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

export async function signOutOtherSessions() {
    return await fetchFromApi(`/sessions/sign-out-others`, {
        method: 'POST',
        credentials: 'include',
    });
}

// Super admin only: end every session of a user (all devices and API tokens)
export async function signOutUserEverywhere(userId) {
    return await fetchFromApi(`/users/${userId}/sessions/sign-out`, {
        method: 'POST',
        credentials: 'include',
    });
}

// WebAuthn / Passkey credentials management (for profile page, session-authenticated)
export async function getWebAuthnCredentials() {
    return await fetchFromApi(`/webauthn/credentials`, {
        method: 'GET',
        credentials: 'include',
    });
}

export async function deleteWebAuthnCredential(credentialId) {
    const res = await apiFetch(`/webauthn/credentials/${credentialId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!res.ok) {
        const err = new Error();
        err.status = res.status;
        try { err.body = await res.json(); } catch { err.body = null; }
        throw err;
    }
    const text = await res.text();
    try { return JSON.parse(text); } catch { return text; }
}

